import { ReactElement, SVGProps } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart as LineChartRechart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Margin } from 'recharts/types/util/types';

import { DetailedTooltip } from '@/components/charts/BarChart';
import { ChartContainer } from '@/components/charts/ChartContainer';
import { cn } from '@/utils/classname';
import { abbreviateNumber } from '@/utils/misc.util';

interface Props {
  type?: 'linear' | 'monotone';
  data: {
    name: string;
    [key: string]: string | number;
  }[];
  lines?: {
    dataKey: string;
    lineClassName: string;
    dotClassName: string;
    stroke?: string; // Explicit stroke color for the line
    strokeDasharray?: string; // Optional stroke dash pattern
  }[];
  dot?: boolean;
  legend?: boolean;
  margin?: Margin;
  xAxisProps?: {
    interval?: number | 'preserveStart' | 'preserveEnd' | 'preserveStartEnd';
    tick?:
      | SVGProps<SVGTextElement>
      | ReactElement<SVGElement>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      | ((props: any) => ReactElement<SVGElement>)
      | boolean;
  };
}

export const LineChart = ({
  type = 'linear',
  data,
  lines = [],
  dot,
  legend,
  xAxisProps,
  margin,
}: Props) => {
  return (
    <ChartContainer>
      <LineChartRechart
        data={data}
        margin={{
          top: 12,
          right: 0,
          left: -36,
          bottom: 0,
          ...margin,
        }}
      >
        <CartesianGrid
          strokeDasharray="0 0"
          vertical={false}
          className="stroke-chariot-stroke"
          strokeWidth={2}
        />
        <XAxis
          dataKey="name"
          minTickGap={40}
          className="[&_line]:stroke-chariot-stroke [&_tspan]:fill-chariot-text-secondary text-xs font-normal"
          // tickLine={false}
          strokeWidth={2}
          tickMargin={4}
          {...xAxisProps}
        />
        <YAxis
          className="[&_tspan]:fill-chariot-text-secondary text-xs font-normal"
          axisLine={false}
          tickLine={false}
          width={72}
          tickFormatter={(value: number) => {
            return abbreviateNumber(value).toString();
          }}
        />
        <Tooltip
          content={<DetailedTooltip />}
          cursor={{ className: 'fill-chariot-background-secondary' }}
        />
        {legend && (
          <Legend
            iconSize={0}
            wrapperStyle={{ left: 0, width: '100%' }}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formatter={(value, entry: any) => {
              const line = lines.find(l => l.dataKey === entry?.dataKey);
              return (
                <div className="text-chariot-text-secondary flex items-center gap-2 text-xs font-normal">
                  <div
                    className="h-[6px] w-6 rounded-md"
                    style={{ backgroundColor: line?.stroke }}
                  ></div>
                  {value}
                </div>
              );
            }}
          />
        )}
        {lines.map(
          ({
            dataKey,
            lineClassName,
            dotClassName,
            stroke,
            strokeDasharray,
          }) => (
            <Line
              type={type}
              dataKey={dataKey}
              className={lineClassName}
              key={dataKey}
              legendType="plainline"
              stroke={stroke}
              strokeDasharray={strokeDasharray}
              dot={
                dot
                  ? {
                      className: cn(dotClassName),
                      strokeWidth: 6,
                      r: 3,
                      fill: stroke,
                      stroke: stroke,
                    }
                  : { className: cn(dotClassName), r: 0 }
              }
              strokeWidth={4}
            />
          )
        )}
      </LineChartRechart>
    </ChartContainer>
  );
};
